import { Fragment, ReactNode } from 'react';
import { ArrowRight, Info, Lightbulb, AlertTriangle, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { InsightBlock } from '@/data/insights';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

// Minimal inline markdown → React: **bold**, *italic*, [text](url).
const renderInline = (input: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(input)) !== null) {
    if (match.index > last) nodes.push(input.slice(last, match.index));
    if (match[1]) {
      nodes.push(<strong key={`b-${key++}`} className="font-semibold text-foreground">{match[1]}</strong>);
    } else if (match[2]) {
      nodes.push(<em key={`i-${key++}`}>{match[2]}</em>);
    } else if (match[3] && match[4]) {
      const url = match[4];
      const external = /^https?:\/\//i.test(url);
      nodes.push(
        <a
          key={`a-${key++}`}
          href={url}
          className="text-primary underline underline-offset-2 hover:no-underline"
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {match[3]}
        </a>,
      );
    }
    last = regex.lastIndex;
  }
  if (last < input.length) nodes.push(input.slice(last));
  return nodes;
};

const calloutStyles = {
  info: {
    icon: Info,
    wrapper: 'bg-primary/5 border-primary/30',
    iconClass: 'text-primary',
  },
  tip: {
    icon: Lightbulb,
    wrapper: 'bg-primary/5 border-primary/30',
    iconClass: 'text-primary',
  },
  warning: {
    icon: AlertTriangle,
    wrapper: 'bg-amber-50 border-amber-300 dark:bg-amber-950/30 dark:border-amber-800',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
} as const;

const InsightContent = ({ blocks }: { blocks: InsightBlock[] }) => {
  const imageSizes = '(min-width: 1024px) 768px, 100vw';

  return (
    <div className="space-y-8">
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'lead':
            return (
              <p key={idx} className="text-xl md:text-2xl leading-relaxed text-foreground font-medium">
                {renderInline(block.text)}
              </p>
            );
          case 'heading':
            return (
              <h2
                key={idx}
                className="text-2xl md:text-3xl font-display font-bold text-foreground mt-6 scroll-mt-24"
              >
                {block.text}
              </h2>
            );
          case 'paragraph':
            return (
              <p key={idx} className="text-lg leading-relaxed text-muted-foreground">
                {renderInline(block.text)}
              </p>
            );
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag
                key={idx}
                className={`space-y-3 text-lg leading-relaxed text-muted-foreground pl-6 ${
                  block.ordered ? 'list-decimal marker:text-primary marker:font-semibold' : 'list-disc marker:text-primary'
                }`}
              >
                {block.items.map((item, i) => (
                  <li key={i} className="pl-1">{renderInline(item)}</li>
                ))}
              </Tag>
            );
          }
          case 'callout': {
            const variant = block.variant || 'info';
            const style = calloutStyles[variant];
            const Icon = style.icon;
            return (
              <aside
                key={idx}
                className={`rounded-2xl border p-6 flex gap-4 ${style.wrapper}`}
              >
                <Icon className={`w-6 h-6 shrink-0 mt-0.5 ${style.iconClass}`} aria-hidden="true" />
                <div className="space-y-1">
                  {block.title && (
                    <div className="font-display font-semibold text-foreground">{block.title}</div>
                  )}
                  <p className="text-base leading-relaxed text-foreground/85">
                    {renderInline(block.text)}
                  </p>
                </div>
              </aside>
            );
          }
          case 'image':
            return (
              <figure key={idx} className="my-4">
                <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
                  <img
                    src={buildSrc(block.base, 1024)}
                    srcSet={buildSrcSet(block.base, IMAGE_WIDTHS.card)}
                    sizes={imageSizes}
                    alt={block.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto"
                  />
                </div>
                {block.caption && (
                  <figcaption className="mt-3 text-sm text-muted-foreground text-center italic">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'table':
            return (
              <div key={idx} className="overflow-x-auto rounded-2xl border border-border shadow-sm">
                <table className="w-full text-left text-base">
                  <thead className="bg-muted/50">
                    <tr>
                      {block.headers.map((h, i) => (
                        <th key={i} className="px-4 py-3 font-display font-semibold text-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r} className="border-t border-border">
                        {row.map((cell, c) => (
                          <td
                            key={c}
                            className={`px-4 py-3 align-top ${
                              c === 0 ? 'font-semibold text-foreground' : 'text-muted-foreground'
                            }`}
                          >
                            {renderInline(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {block.caption && (
                  <div className="px-4 py-2 text-sm text-muted-foreground italic border-t border-border bg-muted/20">
                    {block.caption}
                  </div>
                )}
              </div>
            );
          case 'quote':
            return (
              <blockquote
                key={idx}
                className="relative rounded-2xl bg-muted/40 p-6"
              >
                <Quote className="absolute -top-3 -left-3 w-8 h-8 text-primary/60" aria-hidden="true" />
                <p className="text-lg italic text-foreground leading-relaxed">
                  {renderInline(block.text)}
                </p>
                {block.cite && (
                  <cite className="mt-3 block not-italic text-sm text-muted-foreground">
                    — {block.cite}
                  </cite>
                )}
              </blockquote>
            );
          case 'faq':
            return (
              <div key={idx} className="space-y-4">
                {block.items.map((item, i) => (
                  <details
                    key={i}
                    className="group rounded-2xl border border-border bg-card p-5 shadow-sm open:shadow-md transition-shadow"
                  >
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-card-foreground">
                      <span>{item.question}</span>
                      <span className="text-primary text-2xl leading-none group-open:rotate-45 transition-transform">
                        +
                      </span>
                    </summary>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                      {renderInline(item.answer)}
                    </p>
                  </details>
                ))}
              </div>
            );
          case 'cta':
            return (
              <div
                key={idx}
                className="my-4 rounded-2xl bg-foreground text-background p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xl"
              >
                <div>
                  <h3 className="text-xl md:text-2xl font-display font-bold mb-2">{block.title}</h3>
                  <p className="text-background/80 leading-relaxed">{renderInline(block.text)}</p>
                </div>
                <Link
                  to={block.to}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg whitespace-nowrap"
                >
                  {block.label}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            );
          default:
            return <Fragment key={idx} />;
        }
      })}
    </div>
  );
};

export default InsightContent;
